<template>
    <form
        :class="viewport.is"
        :style="{ '--heading': headingFont }"
        @submit.prevent="submit"
    >
        <div v-if="course.test_mode.payment_links" class="test-mode">
            <div>
                <Logo />
                <Icon name="wrench" size="12" />
                <Txt size="marker">
                    Test mode
                </Txt>
            </div>
        </div>
        <section class="course-details" :style="{ backgroundColor, color: textColour, '--color-rgb': textColourRgb }">
            <div v-if="viewport.is.tablet" class="gradient" />
            <div v-if="course">
                <img
                    v-if="course.theme.logo && course.theme.logo.enabled"
                    :src="course.theme.logo.asset"
                    :alt="course.theme.logo.alt"
                    class="logo"
                />
                <Photo
                    v-if="course.thumbnail && !isGif"
                    :src="course.thumbnail"
                    cover
                />
                <ScreenPreview
                    v-else
                    :screen="startScreen"
                    :course="course"
                    simple
                />
                <div>
                    <Txt :size="viewport.is.tablet ? 'xs' : 's'" heading>
                        {{ course.title }}
                    </Txt>
                    <Txt v-if="viewport.is.tablet" size="xl" heading>
                        {{ total }}
                    </Txt>
                </div>
                <div v-if="!viewport.is.tablet" class="pricing">
                    <hr />
                    <div class="pricing-row">
                        <Txt size="xs" heading>
                            Subtotal
                        </Txt>
                        <Txt size="xs" heading :class="{ skeleton: !tax }">
                            {{ subtotal }}
                        </Txt>
                    </div>
                    <div class="pricing-row">
                        <Txt>
                            {{ taxType }}
                        </Txt>
                        <Txt :class="{ skeleton: !tax }">
                            {{ taxAmount }}
                        </Txt>
                    </div>
                    <hr />
                    <div class="pricing-row">
                        <Txt size="xs" heading>
                            Total
                        </Txt>
                        <Txt size="xs" heading :class="{ skeleton: !tax }">
                            {{ total }}
                        </Txt>
                    </div>
                </div>
            </div>
        </section>
        <section class="identify-form" :style="{ '--primary': course.theme.colours.standard.primary }">
            <div v-if="isCheckingCode">
                <Txt size="s" heading>
                    One last step!
                </Txt>
                <Txt size="s">
                    Check your inbox for a verification code sent to {{ email }}
                </Txt>
                <Field
                    ref="code"
                    v-model:value="code"
                    type="number"
                    name="code"
                    required
                    inline-label
                    show-info-always
                    autofocus
                    :strict="false"
                    :show-controls="false"
                    @valid="isValid = $event"
                >
                    Enter 6 digit verification code
                    <template #error>
                        <Txt v-if="status === 'error'" size="xxs">
                            <Icon name="danger" size="16" class="text-danger" />
                            The code provided was invalid.
                        </Txt>
                    </template>
                </Field>
                <Button
                    type="submit"
                    colour="smoke"
                    size="xxs" heading
                    fill
                    :disabled="!isValid"
                    :status="status"
                >
                    Verify
                </Button>
                <Button
                    icon="arrow-left"
                    icon-pos="left"
                    variant="basic"
                    colour="anchor"
                    size="xxs" heading
                    fill
                    @click="back"
                >
                    Back
                </Button>
                <Txt size="s" class="anchor">
                    Can’t find it? Make sure you check your spam folder.<br />
                    <a @click="sendCode">Resend code</a>
                </Txt>
            </div>
            <div v-else-if="isReturningUser">
                <Txt size="s" heading>
                    Your course is waiting 👋
                </Txt>
                <Txt size="s">
                    Please enter your email so we can check if you already have access to this course. 
                </Txt>
                <Field
                    v-model:value="email"
                    type="email"
                    name="email"
                    autocomplete="email"
                    placeholder="name@email.com"
                    show-info-always
                    inline-label
                    required
                    @valid="isValid = $event"
                    @update:value="error = null"
                >
                    Enter email address
                    <template #info>
                        <Txt v-if="error" size="xxs">
                            <Icon name="info" size="16" right class="text-danger" />
                            <span>
                                {{ error }}
                            </span>
                        </Txt>
                    </template>
                </Field>
                <Button
                    type="submit"
                    size="xs" heading
                    fill
                    :colour="course.theme.colours.standard.primary"
                    :disabled="!isValid"
                    :status="status"
                >
                    Continue
                </Button>
                <Button
                    icon="arrow-left"
                    icon-pos="left"
                    variant="basic"
                    colour="anchor"
                    size="xxs" heading
                    @click="isReturningUser = false"
                >
                    Back
                </Button>
            </div>
            <div v-else class="checkout">
                <Transition name="fadeslow">
                    <form v-show="isCheckoutReady" @submit.prevent="purchase">
                        <Field
                            v-model:value="email"
                            type="email"
                            name="email"
                            autocomplete="email"
                            placeholder="Enter email address"
                            show-info-always
                            required
                            :disabled="status"
                            @valid="isValid = $event"
                            @update:value="showPurchaseWarning = false"
                        >
                            <Txt size="xxs" heading>
                                Email address
                            </Txt>
                            <template #info>
                                <Txt v-if="showPurchaseWarning && email" size="xxs">
                                    <Icon name="info" size="16" right />
                                    <span>
                                        It looks like you have already purchased this course, please verify your email to access it again. <a @click="sendCode(); isCheckingCode = true;">Get the verification code.</a>
                                    </span>
                                </Txt>
                            </template>
                        </Field>
                        <Txt size="xxs" heading>
                            Pay with
                        </Txt>
                        <div ref="checkout" class="checkout" />
                        <div v-if="viewport.is.tablet" class="pricing">
                            <div class="course">
                                <Photo
                                    v-if="course.thumbnail && !isGif"
                                    :src="course.thumbnail"
                                    cover
                                />
                                <ScreenPreview
                                    v-else
                                    :screen="startScreen"
                                    :course="course"
                                    simple
                                />
                                <Txt size="xs" heading>
                                    {{ course.title }}
                                </Txt>
                            </div>
                            <hr />
                            <div class="pricing-row">
                                <Txt size="xs" heading>
                                    Subtotal
                                </Txt>
                                <Txt size="xs" heading :class="{ skeleton: !tax }">
                                    {{ subtotal }}
                                </Txt>
                            </div>
                            <div class="pricing-row">
                                <Txt>
                                    {{ taxType }}
                                </Txt>
                                <Txt :class="{ skeleton: !tax }">
                                    {{ taxAmount }}
                                </Txt>
                            </div>
                            <hr />
                            <div class="pricing-row">
                                <Txt size="xs" heading>
                                    Total
                                </Txt>
                                <Txt size="xs" heading :class="{ skeleton: !tax }">
                                    {{ total }}
                                </Txt>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            size="xxs" heading
                            fill
                            :disabled="course.test_mode_payment_links || showPurchaseWarning || !isPaymentDetailsValid || !isValid"
                            :colour="course.theme.colours.standard.primary"
                            :status="status"
                            class="visually-enabled"
                        >
                            Pay {{ total }}
                        </Button>
                        <Button
                            variant="basic"
                            colour="anchor"
                            size="xxs" heading
                            @click="isReturningUser = true"
                        >
                            Already paid?
                        </Button>
                    </form>
                </Transition>
                <Txt size="xs">
                    <a href="https://coassemble.com/privacy-policy" target="_blank">Privacy Policy</a>
                </Txt>
                <div v-if="course.test_mode.payment_links" class="test-overlay">
                    <Icon name="info" class="text-warning" />
                    <Txt size="xs" heading>
                        Ready to sell?
                    </Txt>
                    <Txt>
                        If you’re ready to start selling your courses, and want to remove all test mode restrictions then you’ll need to <a href="/dashboard?flow=paymentlinks">setup payments with Stripe</a>
                        {{ course.upgraded ? '.' : ' and start a paid subscription.' }}
                    </Txt>
                </div>
            </div>
        </section>
    </form>
</template>

<script>
import { loadFont } from '@/store/theme';
import { startScreen } from '@/helpers/course';
import { Currency } from '@depay/local-currency';
import { useResponsive } from '@/store/responsive';
import { readable, hexToRgb } from '@/helpers/colour';

import Txt from '@/components/form/txt';
import Field from '@/components/form/field';
import Icon from '@/components/utility/icon';
import Photo from '@/components/media/photo';
import Button from '@/components/utility/button';
import ScreenPreview from '@/components/author/screen-preview';

import Logo from '@/assets/CO_Logo_H_RGB.svg';

export default {
    name: 'CoursePaywall',
    components: { Txt, Field, Photo, Icon, Button, ScreenPreview, Logo },
    inject: ['$global', '$stripe'],
    props: {
        course: { type: Object, default: null }
    },
    emits: ['purchased', 'authorised'],
    data() {
        return {
            email: '',
            code: '',
            
            elements: null,
            paymentElement: null,

            token: null,
            tax: null,
            status: null,
            error: null,
            isValid: false,
            isPaymentDetailsValid: false,
            isReturningUser: false,
            isCheckingCode: false,
            isCheckoutReady: false,
            showPurchaseWarning: false,
            checkForPurchaseTimeout: null,
            paymentId: null,

            viewport: useResponsive()
        };
    },
    computed: {
        key() {
            return this.$route.params.key || '';
        },
        keyType() {
            return this.key.length === 32 ? 'key' : 'id';
        },
        startScreen() {
            return startScreen(this.course);
        },
        headingFont() {
            return this.course
                && this.course.theme
                && this.course.theme.font;
        },
        isGif() {
            return this.course.image
                && this.course.image.includes('.gif');
        },
        hasTheme() {
            return this.course
                && this.course.theme
                && this.course.theme.colours
                && !!this.course.theme.colours.standard;
        },
        backgroundColor() {
            if (this.hasTheme) return this.course.theme.colours.standard.background;
            return 'var(--cloud)';
        },
        textColour() {
            return readable(hexToRgb(this.backgroundColor)).contrast;
        },
        textColourRgb() {
            return hexToRgb(this.textColour).join(', ');
        },
        appearance() {
            return {
                theme: 'stripe',
                variables: {
                    colorPrimary: this.course.theme.colours.standard.primary,
                    colorBackground: '#FFF',
                    colorText: '#0F171F',
                    colorTextPlaceholder: '#919AA8',
                    colorDanger: '#F9423A',
                    fontFamily: '"Roboto", sans-serif',
                    fontLineHeight: '1.5',
                    fontSizeSm: '14px',
                    fontSizeBase: '14px',
                    fontSmooth: 'always',
                    spacingUnit: '4px',
                    borderRadius: '8px',
                },
                rules: {
                    '.Input': {
                        boxShadow: 'none',
                        lineHeight: '1.5',
                        padding: '11px 16px',
                        border: '1px solid #CACED5',
                    },
                    '.Input:focus': {
                        boxShadow: 'none'
                    },
                    '.Label': {
                        padding: '2px 0 10px',
                        margin: '0',
                        color: '#919AA8'
                    }
                }
            };
        },
        appearanceDisabled() {
            return {
                ...this.appearance,
                variables: {
                    ...this.appearance.variables,
                    colorPrimary: '#CACED5',
                    colorBackground: '#F6F7F8'
                },
                rules: {
                    ...this.appearance.rules,
                    '.Input:focus': {
                        ...this.appearance.rules['.Input:focus'],
                        border: '1px solid #CACED5'
                    }
                }
            };
        },
        subtotal() {
            if (!this.tax) return '$0.00';
            let amount = this.course.price;
            if (this.tax.subtotal) amount = this.tax.subtotal / 100;
            return new Currency({ amount, code: this.tax.currency }).toString({ minimumFractionDigits: 0 });
        },
        taxAmount() {
            if (!this.tax) return '$0.00';
            let amount = 0;
            if (this.tax.amount) amount = this.tax.amount / 100;
            return new Currency({ amount, code: this.tax.currency }).toString({ minimumFractionDigits: 0 });
        },
        taxType() {
            return this.tax && this.tax.type ? this.tax.type.toUpperCase() : 'Tax';
        },
        total() {
            if (!this.tax) return '$' + this.course.price + '.00';
            return new Currency({ amount: this.course.price, code: this.tax.currency }).toString({ minimumFractionDigits: 0 });
        },
    },
    watch: {
        headingFont: {
            handler(to) {
                if (to === 'Poppins') return;
                loadFont(to);
            },
            immediate: true
        },
        isReturningUser(to) {
            if (!to) this.setupCheckout();
        },
        email(to) {
            this.$nextTick(() => {
                if (to && this.isValid && !this.isReturningUser) this.debounceCheckForPurchase();
            });
        }
    },
    mounted() {
        this.$global.loadStripe(true, this.course.stripeAccount ? { stripeAccount: this.course.stripeAccount } : null)
            .then(() => this.setupCheckout());
    },
    methods: {
        submit() {
            if (this.isCheckingCode) return this.checkCode();
            if (this.status) return;
            this.status = 'waiting';
            return this.sendCode()
                .then(({ data: isAuthorised }) => {
                    if (isAuthorised) {
                        this.status = null;
                        this.isValid = false;
                        this.isCheckingCode = true;
                    } else {
                        this.status = 'error';
                        this.error = 'This email does not have access to this course.';
                        this.$timeout(() => this.status = null, 2000);
                    }
                });
        },
        sendCode() {
            return this.$http.get(`/learn/auth/${this.key}`, { params: { email: this.email } });
        },
        debounceCheckForPurchase() {
            if (this.checkForPurchaseTimeout) this.$cancel(this.checkForPurchaseTimeout);
            this.checkForPurchaseTimeout = this.$timeout(() => this.checkForPurchase(), 500);
        },
        checkForPurchase(auto = false) {
            return this.$http
                .get(`/learn/purchased/${this.key}`, { params: { email: this.email } })
                .then(({ data }) => {
                    if (auto) return data.purchased;
                    if (data.purchased) this.showPurchaseWarning = true;
                });
        },
        checkCode() {
            if (this.status) return;
            this.status = 'waiting';
            return this.$http
                .get(`/learn/auth/${this.key}`, { params: { email: this.email, code: this.code } })
                .then(({ data }) => {
                    if (data && data.token) {
                        this.status = null;
                        this.token = data.token;
                        this.$emit('authorised', data.token);
                    } else {
                        this.$refs.code.focus();
                        this.status = 'error';
                        return this.$timeout(() => this.status = null, 2000);
                    }
                });
        },
        setupCheckout() {
            this.getCheckout()
                .then(secret => {
                    const options = {
                        clientSecret: secret,
                        appearance: this.appearance,
                        fonts: [{
                            cssSrc: 'https://fonts.googleapis.com/css2?family=Roboto&display=swap'
                        }]
                    };
                    this.elements = this.$stripe.elements(options);
                    this.paymentElement = this.elements.create('payment', {
                        fields: {
                            billingDetails: {
                                address: { country: 'never' }
                            }
                        }
                    });
                    this.paymentElement.mount(this.$refs.checkout);
                    this.paymentElement.on('change', (event) => {
                        if (event.complete) this.isPaymentDetailsValid = true;
                        this.error = event.error ? event.error.message : null;
                        this.status = null;
                    });
                    this.$timeout(() => this.isCheckoutReady = true, 500);
                });
        },
        getCheckout() {
            return this.$http
                .get(`/learn/checkout/${this.key}`)
                .then(({ data }) => {
                    this.tax = data.tax;
                    this.paymentId = data.id;
                    return data.secret;
                });
        },
        purchase() {
            if (this.course.test_mode_payment_links) return; // Prevent cheeky test mode users from purchasing
            if (!this.isValid) return;
            this.status = 'waiting';
            this.disablePaymentElement();
            return this.$http.post(`/learn/checkout/${this.key}/submit`, { payment: this.paymentId, email: this.email })
                .then(() => {
                    return this.$stripe.confirmPayment({
                        elements: this.elements,
                        confirmParams: {
                            'return_url': window.location.href,
                            'payment_method_data': {
                                'billing_details': {
                                    'email': this.email,
                                    'address': { 'country': this.tax.country }
                                }
                            }
                        },
                        redirect: 'if_required',
                    });
                })
                .then(result => {
                    if (result.error) {
                        this.status = 'error';
                        this.enablePaymentElement();
                    } else {
                        const payment = result.paymentIntent;
                        if (payment.status === 'succeeded') {
                            this.onCheckoutSuccess();
                        } else {
                            this.status = 'error';
                            this.enablePaymentElement();
                        }
                    }
                });
        },
        disablePaymentElement() {
            this.paymentElement.update({ readOnly: true });
            this.elements.update({ appearance: this.appearanceDisabled });
        },
        enablePaymentElement() {
            this.paymentElement.update({ readOnly: false });
            this.elements.update({ appearance: this.appearance });
        },
        onCheckoutSuccess() {
            return this.$http
                .post(`/learn/checkout/${this.key}`, { tax: this.tax.id, email: this.email })
                .then(({ data }) => {
                    this.status = 'success';
                    this.$emit('purchased', data.token);
                });
        },
        back() {
            this.isCheckingCode = false;
            this.isValid = false;
            this.email = '';
            this.status = null;
        }
    }
};
</script>

<style lang="less" scoped>
form {
    flex: 1;
    position: relative;
    display: flex;
    text-align: left;
    .field {
        margin-top: 24px;
        width: 100%;
    }
    .cobutton {
        margin-bottom: 8px;
    }
    .course-details {
        position: relative;
        flex: 1 1 50%;
        background-color: var(--cloud);
        display: flex;
        align-items: center;
        justify-content: center;
        > div {
            display: flex;
            flex-direction: column;
            gap: 24px;
            max-width: 348px;
            width: 100%;
            img.logo {
                max-height: 48px;
                max-width: 132px;
            }
            .photo-wrapper, .screen-preview {
                position: static;
                border-radius: 12px;
                height: 214px;
                overflow: hidden;
                border: 1px solid rgba(~"var(--color-rgb, var(--coal-rgb))", 0.2);
            }
            .txt-component.body.xs { margin-left: 4px; }
        }
    }
    .pricing {
        display: flex;
        flex-direction: column;
        gap: 12px;
        .pricing-row {
            display: flex;
            justify-content: space-between;
        }
        hr {
            border: 0;
            border-top: 1px solid rgba(~"var(--color-rgb, var(--coal-rgb))", 0.2);
            margin: 12px 0;
        }
    }
    .identify-form {
        position: relative;
        flex: 1 1 50%;
        background-color: #FFF;
        box-shadow: var(--shadow-lg);
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        > div {
            display: flex;
            flex-direction: column;
            max-width: 316px;
            .txt-component.heading.s { margin-bottom: 12px; }
            &.checkout {
                width: 100%;
                height: 100%;
                max-width: 100%;
                justify-content: center;
                position: relative;
                .txt-component.heading.s { margin-bottom: 0; }
                form {
                    flex-direction: column;
                    flex: 0;
                    gap: 0;
                    margin: auto;
                    max-width: 320px;
                    width: 100%;
                }
                .checkout {
                    margin: 16px 0 24px;
                }
            }
            .txt-component.body.xs {
                position: absolute;
                bottom: 24px;
                left: 50%;
                transform: translateX(-50%);

                text-align: center;
                a {
                    color: var(--anchor);
                    text-decoration: underline;
                }
            }
        }
        .test-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            background: rgba(255, 255, 255, 0.8);
            backdrop-filter: blur(4px);
            z-index: 1;
            gap: 12px;
            .txt-component { max-width: 412px; }
        }
    }
    .test-mode {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 999;
        height: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: var(--warning);
        filter: drop-shadow(0px 5px 20px rgba(var(--warning-rgb), 0.04))
            drop-shadow(0px 1px 6px rgba(var(--warning-rgb), 0.06))
            drop-shadow(0px 0px 1px rgba(var(--warning-rgb), 0.04));
        div {
            position: absolute;
            top: 0;
            display: flex;
            align-items: center;
            border-radius: 0 0 8px 8px;
            padding: 12px 16px;
            background: var(--warning);
            color: var(--navy);
            gap: 4px;
            svg {
                width: 96px;
            }
            &:before, &:after {
                content: '';
                position: absolute;
                top: 8px;
                width: 8px;
                height: 8px;
                background: var(--warning);
                mask-image: radial-gradient(circle 14px at 0 0, transparent 50%, black 50%);
            }
            &:before {
                left: -8px;
                transform: rotate(-90deg);
            }
            &:after {
                right: -8px;
                transform: rotate(180deg);
            }
        }
    }
    &.tablet {
        height: 100dvh;
        background-color: #FFF;
        flex-direction: column;
        overflow: auto;
        .course-details, .identify-form {
            flex: 0 0 auto;
            padding: 24px;
            background-color: #FFF;
            box-shadow: none;
            padding-bottom: 0;
            > div {
                position: relative;
                width: calc(100vw - 48px);
                align-items: center;
                text-align: center;
                .photo-wrapper, .screen-preview {
                    height: 128px;
                    aspect-ratio: 209 / 128;
                }
                .pricing {
                    .course {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        > .photo-wrapper, > .screen-preview {
                            width: 72px;
                            height: 48px;
                            border-radius: 8px;
                            overflow: hidden;
                        }
                        .txt-component.heading.xs { margin-bottom: 0; }
                    }
                }
                .cobutton.default {
                    margin-top: 24px;
                }
                .txt-component.body.xs {
                    position: static;
                    transform: none;
                    margin-top: 32px;
                }
                .field {
                    margin-top: 16px;
                }
                img.logo {
                    max-height: 32px;
                    max-width: 88px;
                }
            }
            > .gradient {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
                width: auto;
                max-width: unset;
            }
        }
        .course-details {
            border-bottom: 1px solid var(--smoke);
            padding-bottom: 24px;
        }
        .identify-form > div { max-width: none; }
    }
}
</style>

<style lang="less">
form .test-mode {
    svg {
        path { fill: var(--coal); }
    }
}
.checkout {
    .txt-component.body a {
        font-weight: 700;
        text-decoration: underline;
        color: var(--coal);
    }
}
</style>
